import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tipoAreaService } from '../services/tipoAreaService';
import { CreateTipoAreaForm, UpdateTipoAreaForm, UpdateTareaTipoAreaConfigDto, CreateTareaTipoAreaDto, TareaSimple, TipoArea } from '../types';
import { useCrudResource } from './useCrudResource';

export const useTipoAreas = () => {
  const crud = useCrudResource<TipoArea, CreateTipoAreaForm, UpdateTipoAreaForm>({
    queryKey: ['tipoAreas'],
    fetchList: tipoAreaService.getAll,
    create: tipoAreaService.create,
    update: (id, tipoArea) => tipoAreaService.update(id, tipoArea),
    remove: tipoAreaService.delete,
  });

  // Obtener tipos de área activos
  const { data: tipoAreasActivos = [], isLoading: isLoadingActivos } = useQuery({
    queryKey: ['tipoAreas', 'activos'],
    queryFn: tipoAreaService.getActivos,
  });

  const updateTipoArea = (
    vars: { id: number; tipoArea: UpdateTipoAreaForm },
    options?: Parameters<typeof crud.update>[1]
  ) => crud.updateAsync({ id: vars.id, data: vars.tipoArea }, options);

  return {
    tipoAreas: crud.items,
    tipoAreasActivos,
    isLoading: crud.isLoading,
    isLoadingActivos,
    error: crud.error,
    createTipoArea: crud.createAsync,
    updateTipoArea,
    deleteTipoArea: crud.removeAsync,
    isCreating: crud.isCreating,
    isUpdating: crud.isUpdating,
    isDeleting: crud.isRemoving,
  };
};

// Hook para gestionar tareas de un tipo de área específico
export const useTareasTipoArea = (tipoAreaId: number) => {
  const queryClient = useQueryClient();

  // Obtener tareas (TareaSimple[]) del tipo de área
  const { data: tareasTipoArea = [], isLoading, error } = useQuery<TareaSimple[]>({
    queryKey: ['tareasTipoArea', tipoAreaId],
    queryFn: () => tipoAreaService.getTareasTipoArea(tipoAreaId),
    enabled: !!tipoAreaId,
  });

  // Agregar tarea al tipo de área (crea tarea nueva en backend según contrato actual)
  const addTareaATipo = useMutation({
    mutationFn: ({ tipoAreaId, payload }: { tipoAreaId: number; payload: CreateTareaTipoAreaDto }) =>
      tipoAreaService.addTareaATipo(tipoAreaId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tareasTipoArea', tipoAreaId] });
      queryClient.invalidateQueries({ queryKey: ['tipoAreas'] });
    },
  });

  // Remover tarea del tipo de área
  const removeTareaDeTipo = useMutation({
    mutationFn: ({ tipoAreaId, tareaId }: { tipoAreaId: number; tareaId: number }) =>
      tipoAreaService.removeTareaDeTipo(tipoAreaId, tareaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tareasTipoArea', tipoAreaId] });
      queryClient.invalidateQueries({ queryKey: ['tipoAreas'] });
    },
  });

  // Actualizar configuración (no disponible en API actual)
  const updateTareaTipoAreaConfig = useMutation({
    mutationFn: ({ tipoAreaId, tareaId, payload }: { tipoAreaId: number; tareaId: number; payload: UpdateTareaTipoAreaConfigDto }) =>
      tipoAreaService.updateTareaTipoAreaConfig(tipoAreaId, tareaId, payload),
  });

  return {
    tareasTipoArea,
    isLoading,
    error,
    addTareaATipo: addTareaATipo.mutateAsync,
    removeTareaDeTipo: removeTareaDeTipo.mutateAsync,
    updateTareaTipoAreaConfig: updateTareaTipoAreaConfig.mutateAsync,
    isAddingTarea: addTareaATipo.isPending,
    isRemovingTarea: removeTareaDeTipo.isPending,
    isUpdatingConfig: updateTareaTipoAreaConfig.isPending,
  };
};
